/*
 * @Author: Yumeng Xue
 * @Date: 2026-02-03
 * @Description: Colormap definitions and utilities
 * @FilePath: /topo-mess/src/core/colormaps.ts
 */

import {
    interpolateMagma,
    interpolateViridis,
    interpolateInferno,
    interpolatePlasma,
    interpolateCividis,
    interpolateTurbo,
    interpolateCool,
    interpolateWarm,
    interpolateCubehelixDefault,
    interpolateBlues,
    interpolateGreens,
    interpolateOranges,
    interpolatePurples,
    interpolateReds,
    interpolateGreys,
} from 'd3-scale-chromatic';

// Colormap 类型定义
export type ColormapName =
    | 'magma'
    | 'viridis'
    | 'inferno'
    | 'plasma'
    | 'cividis'
    | 'turbo'
    | 'cool'
    | 'warm'
    | 'cubehelix'
    | 'blues'
    | 'greens'
    | 'oranges'
    | 'purples'
    | 'reds'
    | 'greys';

// Colormap 元数据
export interface ColormapInfo {
    name: ColormapName;
    label: string;
    category: 'sequential' | 'diverging' | 'cyclical';
    interpolator: (t: number) => string;
}

// 所有可用的 colormaps
export const COLORMAPS: ColormapInfo[] = [
    { name: 'magma', label: 'Magma', category: 'sequential', interpolator: interpolateMagma },
    { name: 'viridis', label: 'Viridis', category: 'sequential', interpolator: interpolateViridis },
    { name: 'inferno', label: 'Inferno', category: 'sequential', interpolator: interpolateInferno },
    { name: 'plasma', label: 'Plasma', category: 'sequential', interpolator: interpolatePlasma },
    { name: 'cividis', label: 'Cividis', category: 'sequential', interpolator: interpolateCividis },
    { name: 'turbo', label: 'Turbo', category: 'sequential', interpolator: interpolateTurbo },
    { name: 'cool', label: 'Cool', category: 'sequential', interpolator: interpolateCool },
    { name: 'warm', label: 'Warm', category: 'sequential', interpolator: interpolateWarm },
    { name: 'cubehelix', label: 'Cubehelix', category: 'sequential', interpolator: interpolateCubehelixDefault },
    { name: 'blues', label: 'Blues', category: 'sequential', interpolator: interpolateBlues },
    { name: 'greens', label: 'Greens', category: 'sequential', interpolator: interpolateGreens },
    { name: 'oranges', label: 'Oranges', category: 'sequential', interpolator: interpolateOranges },
    { name: 'purples', label: 'Purples', category: 'sequential', interpolator: interpolatePurples },
    { name: 'reds', label: 'Reds', category: 'sequential', interpolator: interpolateReds },
    { name: 'greys', label: 'Greys', category: 'sequential', interpolator: interpolateGreys },
];

// 获取 colormap 信息
export function getColormapInfo(name: ColormapName): ColormapInfo {
    const info = COLORMAPS.find((c) => c.name === name);
    if (!info) {
        throw new Error(`Unknown colormap: ${name}`);
    }
    return info;
}

// 获取 colormap 插值器
export function getColormapInterpolator(name: ColormapName): (t: number) => string {
    return getColormapInfo(name).interpolator;
}

/**
 * 使用指定的 colormap 将归一化值 [0,1] 映射为 RGB 颜色
 * @param t 归一化值 [0, 1]
 * @param colormap colormap 名称
 * @param reversed 是否反转 (默认 true，低值=浅色，高值=深色)
 * @returns [r, g, b] 0-255 范围
 */
export function getColormapColor(
    t: number,
    colormap: ColormapName,
    reversed: boolean = true
): [number, number, number] {
    const clamped = Math.max(0, Math.min(1, t));
    const mappedT = reversed ? 1 - clamped : clamped;

    const interpolator = getColormapInterpolator(colormap);
    const colorString = interpolator(mappedT);

    // 防御性检查
    if (!colorString || typeof colorString !== 'string') {
        console.warn('Invalid color from interpolator:', colorString, 'for t:', t);
        return [255, 255, 255];
    }

    // 解析颜色字符串 (可能是 #RRGGBB 或 rgb(r, g, b) 格式)
    let r: number, g: number, b: number;

    if (colorString.startsWith('#')) {
        const hex = colorString.replace('#', '');
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
    } else if (colorString.startsWith('rgb')) {
        const match = colorString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (match) {
            r = parseInt(match[1], 10);
            g = parseInt(match[2], 10);
            b = parseInt(match[3], 10);
        } else {
            return [255, 255, 255];
        }
    } else {
        return [255, 255, 255];
    }

    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
        return [r, g, b];
    }
    return [255, 255, 255];
}

/**
 * 生成 colormap 预览条的颜色数据
 * @param colormap colormap 名称
 * @param steps 采样步数
 * @param reversed 是否反转
 * @returns CSS 渐变字符串
 */
export function generateColormapGradient(
    colormap: ColormapName,
    steps: number = 32,
    reversed: boolean = true
): string {
    const colors: string[] = [];

    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const [r, g, b] = getColormapColor(t, colormap, reversed);
        colors.push(`rgb(${r}, ${g}, ${b})`);
    }

    return `linear-gradient(to right, ${colors.join(', ')})`;
}

/**
 * 生成 colormap 的 RGB 数组 (用于 GPU 纹理)
 * @param colormap colormap 名称
 * @param steps 采样步数 (通常 256)
 * @param reversed 是否反转
 * @returns Uint8Array [r0, g0, b0, r1, g1, b1, ...]
 */
export function generateColormapLUT(
    colormap: ColormapName,
    steps: number = 256,
    reversed: boolean = true
): Uint8Array {
    const lut = new Uint8Array(steps * 3);

    for (let i = 0; i < steps; i++) {
        const t = i / (steps - 1);
        const [r, g, b] = getColormapColor(t, colormap, reversed);
        lut[i * 3] = r;
        lut[i * 3 + 1] = g;
        lut[i * 3 + 2] = b;
    }

    return lut;
}
